import { useMutation } from '@apollo/client';
import { Button, Form, Input, Space, Typography } from 'antd';
import React, { useEffect, useMemo } from 'react';
import {
  MAX_LENGTHS,
  MODULE_TYPES,
  STATUS_TYPES
} from '../../../../../common/constants';
import { formValidatorRules } from '../../../../../common/utils';
import HtmlEditor from '../../../../../components/HtmlEditor';
import { FORM_TYPES } from '../../../context/EditPageProvider';
import {
  CREATE_PAGE_MODULE,
  UPDATE_PAGE_MODULE
} from '../../../graphql/Mutations';
import { ModuleFields, Permissions, ShowFields } from './FormInputs';

const getInitialValues = () => ({
  title: '',
  description: '',
  status: STATUS_TYPES.PUBLISHED,
  permissions: [],
  settings: {
    buttonText: true,
    qrCodeText: true
  },
  config: {
    qrCodeUrl: '',
    qrCodeText: '',
    appStoreUrl: '',
    playStoreUrl: '',
    buttonText: ''
  }
});

const SETTINGS = [
  {
    name: 'qrCodeText',
    label: 'QR Code Description',
    allowedTypes: [MODULE_TYPES.DOWNLOAD_APP]
  },
  {
    name: 'buttonText',
    label: 'Button Description',
    allowedTypes: [MODULE_TYPES.DOWNLOAD_APP]
  }
];

const DownloadAppForm = ({
  form: { type: formType, moduleId, defaultValues, index: order },
  pageId,
  type,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const isEdit = formType === FORM_TYPES.EDIT;
  const initialValues = useMemo(() => getInitialValues(), []);

  const CONFIG_TITLE = {
    [MODULE_TYPES.DOWNLOAD_APP]: 'Download App Configs'
  };

  useEffect(() => {
    form.setFieldsValue({});
  }, [form]);

  const [addEditModule, { loading }] = useMutation(
    isEdit ? UPDATE_PAGE_MODULE : CREATE_PAGE_MODULE
  );

  const MODULE_KEYS = {
    [MODULE_TYPES.DOWNLOAD_APP]: 'downloadAppModule'
  };

  useEffect(() => {
    if (moduleId && formType === FORM_TYPES.EDIT && form) {
      form.setFieldsValue({
        title: defaultValues?.title ?? '',
        description: defaultValues?.description ?? '',
        status: defaultValues?.status ?? STATUS_TYPES.DRAFT,
        permissions:
          defaultValues?.permissions?.map((value) => ({
            label: value,
            value
          })) ?? [],
        settings: {
          ...initialValues.settings,
          ...defaultValues?.moduleData?.settings
        },
        config: {
          ...defaultValues?.moduleData?.config
        }
      });
    }
  }, [moduleId, formType, defaultValues, form, initialValues]);

  const handleSubmit = ({ config, settings, permissions, ...values }) => {
    const key = MODULE_KEYS[type];
    const payload = {
      [key]: {
        settings,
        config: {
          ...config
        }
      },
      ...(!isEdit && {
        type,
        order: order + 1
      }),
      permissions: permissions?.map(({ value }) => value),
      status: values?.status ?? null,
      title: values?.title ?? null,
      description: values?.description ?? null
    };
    addEditModule({
      variables: { data: payload, id: isEdit ? moduleId : pageId }
    }).then(
      ({
        data: {
          addUpdatedPageModule: { pageModule }
        }
      }) => {
        onSuccess(pageModule);
      }
    );
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <ModuleFields isTrialSetup />
      <Space className="w-full" direction="vertical">
        <ShowFields settings={SETTINGS} type={type} />
      </Space>
      <Space className="w-full" direction="vertical">
        <Typography.Text>{CONFIG_TITLE[type]}</Typography.Text>
        <div>
          <Form.Item
            label="QR Code Url"
            name={['config', 'qrCodeUrl']}
            required
          >
            <Input placeholder="Enter qr-code url" />
          </Form.Item>
          <Form.Item
            label="QR Code Description"
            name={['config', 'qrCodeText']}
            rules={[formValidatorRules?.maxLength(MAX_LENGTHS.DESCRIPTION)]}
          >
            <Input.TextArea placeholder="Enter description" />
          </Form.Item>
          <Form.Item
            label="Playstore Url"
            name={['config', 'playStoreUrl']}
            required
          >
            <Input placeholder="Enter playstore url" />
          </Form.Item>
          <Form.Item
            label="Appstore Url"
            name={['config', 'appStoreUrl']}
            required
          >
            <Input placeholder="Enter appstore url" />
          </Form.Item>
          <Form.Item
            label="Button Description"
            name={['config', 'buttonText']}
            required
          >
            <HtmlEditor className="full-html" height={400} />
          </Form.Item>
        </div>
      </Space>
      <Permissions />
      <div className="d-flex button-section">
        <Space>
          <Form.Item>
            <Button
              disabled={loading}
              loading={loading}
              htmlType="submit"
              className="text-btn mr-8"
              type="text"
            >
              Save
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              type="text"
              disabled={loading}
              className="text-btn2"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Form.Item>
        </Space>
      </div>
    </Form>
  );
};

export default DownloadAppForm;
